import { Injectable, inject } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { PercentPipe } from '../../../pipes/percent.pipe';
import { AssetAllocation } from '../../../domain/lti/investment-portfolio.domain';
import { AssetCategoryGroupType } from '../../../domain/lti/asset-allocation.domain';

@Injectable({ providedIn: 'root' })
export class AssetGroupAllocationChartService {
  private readonly percentPipe = inject(PercentPipe);
  private readonly translateService = inject(TranslateService);

  private static readonly ASSET_CATEGORY_GROUP_COLOR: Map<AssetCategoryGroupType, string> = new Map<AssetCategoryGroupType, string>([
    [AssetCategoryGroupType.EQUITIES, '#008a00'],
    [AssetCategoryGroupType.CASH_AND_FIXED_INCOME, '#933214']
  ]);

  private static readonly NO_DATA_COLOURS: Array<string> = ['#ffffff'];
  private static readonly NO_DATA_BORDER_COLOUR: string = '#cccccc';

  createChartConfiguration(assetCategoryGroupAllocations: Array<AssetAllocation>,
                           size: number,
                           innerSize: number | string,
                           animation: boolean): Highcharts.Options {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    assetCategoryGroupAllocations = this.filterAllocations(assetCategoryGroupAllocations);
    const config: Highcharts.Options = {
      accessibility: {
        enabled: true
      },
      chart: {
        backgroundColor: '#ffffff',
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        type: 'pie',
        margin: [0, 0, 0, 0],
        spacingTop: 0,
        spacingBottom: 0,
        spacingLeft: 0,
        spacingRight: 0,
        height: size,
        width: size
      },
      title: {
        text: ' '
      },
      plotOptions: {
        pie: {
          allowPointSelect: false,
          dataLabels: {
            enabled: false
          },
          colors: this.getPieChartColors(assetCategoryGroupAllocations)
        },
        series: {
          states: {
            inactive: {
              opacity: 1
            },
            hover: {
              halo: {
                size: 0
              },
              enabled: false
            }
          }
        }
      },
      series: [{
        name: 'Asset Allocation',
        data: this.getSeriesDataPoints(assetCategoryGroupAllocations),
        type: 'pie'
      }]
    };
    if (this.isUndefined(assetCategoryGroupAllocations)) {
      config.tooltip = { enabled: false };
    } else {
      config.tooltip = {
        enabled: true,
        formatter(): string {
          // Access the point data safely
          // In Highcharts 12.3.0, we need to handle the data differently
          const pointData = this.points?.[0] || this;
          const args = {
            category: pointData.name || '',
            allocation: self.percentPipe.transform(pointData.y, { format: 'n0' })
          };
          return self.translateService.instant('tdWb.lti.assetAllocationChart.assetCategoryTypeChart.TOOLTIP', args);
        }
      };
    }
    if (this.isUndefined(assetCategoryGroupAllocations)) {
      config.plotOptions.pie.borderColor = AssetGroupAllocationChartService.NO_DATA_BORDER_COLOUR;
    } else {
      config.plotOptions.pie.borderColor = '#ffffff';
    }
    if (!this.isUndefined(assetCategoryGroupAllocations)) {
      config.plotOptions.pie.borderWidth = 3;
    }
    if (innerSize !== undefined) {
      config.plotOptions.pie.innerSize = innerSize;
    }
    if (animation === true) {
      config.plotOptions.series.animation = {
        duration: 1000
      };
    }
    if (!this.isUndefined(assetCategoryGroupAllocations) && assetCategoryGroupAllocations.length === 1) {
      config.plotOptions.pie.endAngle = 359.7; // to have gap when allocation 100%
    }
    return config;
  }

  getAssetCategoryGroupColor(assetCategoryType: AssetCategoryGroupType): string {
    return AssetGroupAllocationChartService.ASSET_CATEGORY_GROUP_COLOR.get(assetCategoryType);
  }

  private filterAllocations(assetCategoryGroupAllocations: Array<AssetAllocation>): Array<AssetAllocation> {
    if (this.isUndefined(assetCategoryGroupAllocations)) {
      return assetCategoryGroupAllocations;
    }
    return assetCategoryGroupAllocations
      .filter((assetAllocation: AssetAllocation): boolean => assetAllocation.targetAllocationPct > 0)
      .slice();
  }

  private getSeriesDataPoints(assetCategoryGroupAllocations: Array<AssetAllocation>): Array<Highcharts.PointOptionsObject> {
    const dataPoints: Array<Highcharts.PointOptionsObject> = new Array<Highcharts.PointOptionsObject>();
    if (this.isUndefined(assetCategoryGroupAllocations)) {
      dataPoints.push({
        name: ' ',
        y: 100
      });
    } else {
      assetCategoryGroupAllocations.forEach((assetAllocation: AssetAllocation): void => {
        dataPoints.push({
          name: this.translateService.instant('tdWb.lti.domain.assetCategoryGroupType.' + assetAllocation.assetCategoryGroupType),
          y: assetAllocation.targetAllocationPct
        });
      });
    }
    return dataPoints;
  }

  private getPieChartColors(assetCategoryGroupAllocations: Array<AssetAllocation>): Array<string> {
    let colours: Array<string> = new Array<string>();
    if (this.isUndefined(assetCategoryGroupAllocations)) {
      colours = AssetGroupAllocationChartService.NO_DATA_COLOURS;
    } else {

      assetCategoryGroupAllocations.forEach((assetAllocation: AssetAllocation): void => {
        colours.push(this.getAssetCategoryGroupColor(assetAllocation.assetCategoryGroupType));
      });
    }
    return colours;
  }

  private isUndefined(assetCategoryGroupAllocations: Array<AssetAllocation>): boolean {
    return assetCategoryGroupAllocations === null || assetCategoryGroupAllocations === undefined;
  }

}

